import styled, { css } from 'styled-components';

const COLOR_GREY_1 = '#171716';
const COLOR_GREY_4 = '#b7b7b7';
const COLOR_GREY_5 = '#deddd4';
const COLOR_WHITE = '#fff';
const COLOR_ACCENT = '#ffb100';

export const Controls = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 12px;
    margin-left: auto;

    @media (max-width: 991px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const BaseButton = styled.button`
    align-items: center;
    border-radius: 8px;
    box-shadow: none;
    cursor: pointer;
    display: inline-flex;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    height: 36px;
    justify-content: center;
    line-height: 1;
    margin: 0;
    padding: 0 10px;
    text-transform: none;
    transition: none;
    white-space: nowrap;

    :disabled {
        cursor: not-allowed;
        opacity: 1;
    }
`;

export const HeaderAuthButton = styled(BaseButton)<{ $alt?: boolean }>`
    background: ${({ $alt }) => ($alt ? COLOR_WHITE : COLOR_GREY_1)};
    border: 2px solid ${COLOR_GREY_1};
    color: ${({ $alt }) => ($alt ? COLOR_GREY_1 : COLOR_WHITE)};

    :disabled {
        border-color: ${COLOR_GREY_4};
        color: ${({ $alt }) => ($alt ? COLOR_GREY_4 : COLOR_WHITE)};
        opacity: 0.6;
    }
`;

export const HeaderMenuLink = styled.a`
    align-items: center;
    background: ${COLOR_WHITE};
    border: 2px solid ${COLOR_GREY_1};
    border-radius: 8px;
    color: ${COLOR_GREY_1};
    display: inline-flex;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    height: 36px;
    justify-content: center;
    line-height: 1;
    padding: 0 10px;
    text-decoration: none;
    white-space: nowrap;
`;

export const HeaderMenuButton = styled(BaseButton)`
    background: ${COLOR_GREY_1};
    border: 0;
    color: ${COLOR_WHITE};
`;

export const ModalContainer = styled.div`
    align-items: center;
    display: flex;
    inset: 0;
    justify-content: center;
    padding: 12px;
    position: fixed;
    z-index: 10000;
`;

export const Backdrop = styled.button.attrs({ type: 'button' })`
    background-color: rgba(0, 0, 0, 0.5);
    border: 0;
    inset: 0;
    padding: 0;
    position: fixed;
    z-index: 0;
`;

export const ModalContent = styled.div`
    background-color: ${COLOR_WHITE};
    border-radius: 8px;
    box-shadow:
        0 3px 1px -2px rgba(0, 0, 0, 0.2),
        0 2px 2px 0 rgba(0, 0, 0, 0.14),
        0 1px 5px 0 rgba(0, 0, 0, 0.12);
    max-height: calc(100dvh - 24px);
    max-width: 350px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 12px 12px 16px;
    position: relative;
    transform: translateZ(0);
    width: calc(100vw - 24px);
    z-index: 1;
    -webkit-overflow-scrolling: touch;

    @media (min-width: 768px) {
        padding: 16px 16px 20px;
        width: 350px;
    }
`;

export const Form = styled.form<{ $loading: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;

    ${({ $loading }) =>
        $loading &&
        css`
            opacity: 0.5;
            pointer-events: none;
            user-select: none;
        `}
`;

export const CloseButton = styled.button.attrs({ type: 'button' })`
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 50%;
    color: ${COLOR_GREY_5};
    cursor: pointer;
    display: inline-flex;
    height: 28px;
    justify-content: center;
    line-height: 1;
    padding: 0;
    position: absolute;
    right: 7px;
    top: 7px;
    width: 28px;
    z-index: 2;

    :disabled {
        cursor: not-allowed;
    }

    :not(:disabled):focus-visible,
    :not(:disabled):hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
`;

export const FormHeader = styled.div<{ $marginTopBottom?: string }>`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: ${({ $marginTopBottom }) => $marginTopBottom || '20px 0'};
    width: 100%;
`;

export const FormFields = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    margin-bottom: 20px;
    width: 100%;
`;

export const Row = styled.div<{ $gap?: number; $marginBottom?: number; $justify?: string }>`
    align-items: center;
    display: flex;
    gap: ${({ $gap }) => ($gap === undefined ? 8 : $gap)}px;
    justify-content: ${({ $justify }) => $justify || 'center'};
    margin-bottom: ${({ $marginBottom }) => ($marginBottom === undefined ? 0 : $marginBottom)}px;
    width: 100%;
`;

export const Title = styled.h2`
    color: ${COLOR_GREY_1};
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    margin: 0;
    text-align: center;
`;

export const Text = styled.p<{
    $center?: boolean;
    $error?: boolean;
    $size?: number;
    $weight?: number;
}>`
    color: ${({ $error }) => ($error ? '#d32f2f' : COLOR_GREY_1)};
    font-family: Roboto, sans-serif;
    font-size: ${({ $size }) => $size || 14}px;
    font-weight: ${({ $weight }) => $weight || 400};
    line-height: ${({ $size }) => ($size === 18 ? '24px' : '20px')};
    margin: 0;
    text-align: ${({ $center }) => ($center ? 'center' : 'inherit')};
`;

export const TextField = styled.input`
    background-color: ${COLOR_WHITE};
    border: 1px solid ${COLOR_GREY_5};
    border-radius: 8px;
    color: ${COLOR_GREY_1};
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.333;
    margin: 0;
    min-height: 40px;
    padding: 0 20px;
    width: 100%;

    :focus {
        border-color: ${COLOR_GREY_1};
        outline: 0;
    }
`;

export const CheckboxWrapper = styled.label<{ $size?: number }>`
    align-items: center;
    cursor: pointer;
    display: inline-flex;
    gap: 8px;

    > input {
        accent-color: ${COLOR_GREY_1};
        cursor: pointer;
        margin: 0;
    }

    > span {
        color: ${COLOR_GREY_1};
        font-family: Roboto, sans-serif;
        font-size: ${({ $size }) => $size || 14}px;
        font-weight: 400;
        line-height: ${({ $size }) => ($size === 12 ? 14 : 20)}px;
    }
`;

export const ModalDivider = styled.div<{ $margin?: string }>`
    align-items: center;
    color: #505050;
    display: flex;
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 400;
    gap: 12px;
    line-height: 1;
    margin: ${({ $margin }) => $margin || '0'};
    width: 100%;

    :before,
    :after {
        background-color: ${COLOR_GREY_5};
        content: '';
        flex: 1;
        height: 1px;
    }
`;

export const OAuthButton = styled(BaseButton)`
    background: ${COLOR_WHITE};
    border: 2px solid ${COLOR_GREY_1};
    color: ${COLOR_GREY_1};
    gap: 8px;
    height: 48px;
    width: 100%;
`;

export const MassiveButton = styled(BaseButton)`
    background: ${COLOR_ACCENT};
    border: 2px solid ${COLOR_ACCENT};
    border-radius: 28px;
    color: #000;
    font-size: 20px;
    height: 56px;
    padding: 0 32px;

    :disabled {
        background: #ffd266;
        border-color: #ffd266;
        color: rgba(0, 0, 0, 0.5);
    }
`;

export const ContainedButton = styled(BaseButton)`
    background: ${COLOR_GREY_1};
    border: 0;
    color: ${COLOR_WHITE};
`;

export const TextActionButton = styled.button`
    background: transparent;
    border: 0;
    color: ${COLOR_GREY_1};
    cursor: pointer;
    font-family: Roboto, sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    padding: 0;
    position: relative;

    :disabled {
        color: ${COLOR_GREY_4};
        cursor: not-allowed;
    }

    :after {
        background-image: linear-gradient(transparent 60%, #f6b32a 60%);
        bottom: 1px;
        content: '';
        height: 5px;
        left: -2px;
        position: absolute;
        right: -2px;
        z-index: -1;
    }
`;

export const SectionRule = styled.div`
    background-color: ${COLOR_GREY_5};
    height: 1px;
    margin: 20px 12px;
    width: calc(100% - 24px);
`;

export const CenteredColumn = styled.div<{ $gap?: number }>`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: ${({ $gap }) => ($gap === undefined ? 0 : $gap)}px;
`;

export const OAuthIconWrap = styled.span`
    display: inline-flex;
    height: 20px;
    width: 20px;
`;
